import { Phone, Mail, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, LineReveal, Counter } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { Testimonials } from "@/components/site/Testimonials";
import { CONTACT, REGIONS, IMG } from "@/lib/content";

const STATS = [
  { v: 2013, label: "Founded", plain: true },
  { v: 4000, suffix: "+", label: "Businesses Australia-wide" },
  { v: 766, suffix: "+", label: "CEC-verified engineers" },
  { v: 9, label: "Regional teams" },
];

const Person = ({ p, i }) => (
  <FadeUp delay={i * 0.1} data-testid={`team-${p.id}`} className="rounded-2xl bg-white border border-black/5 p-8 lg:p-10 flex flex-col">
    <div className="h-20 w-20 rounded-full bg-[var(--navy)] grid place-items-center font-display font-black text-3xl text-[var(--sun)]">
      {p.name.split(" ").map((x) => x[0]).join("")}
    </div>
    <p className="mt-6 font-mono text-xs tracking-[0.2em] uppercase text-[#0057c2]">{p.role}</p>
    <h3 className="mt-2 font-display font-black uppercase text-4xl">{p.name}</h3>
    <p className="mt-4 text-slate-700 leading-relaxed">{p.bio}</p>
    <div className="mt-auto pt-8 flex flex-col gap-2">
      <a href={p.href} data-testid={`team-${p.id}-phone`} className="flex items-center gap-2 font-display font-bold text-xl hover:text-[#0057c2] transition-colors"><Phone size={16} /> {p.phone}</a>
      <a href={`mailto:${p.email}`} data-testid={`team-${p.id}-email`} className="flex items-center gap-2 text-slate-700 hover:text-[#0057c2] transition-colors break-all"><Mail size={16} /> {p.email}</a>
    </div>
  </FadeUp>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero testId="about-hero" eyebrow="About us" lines={["A family", "business, since", "2013."]} intro="Carwash Solar Advisers is a proud division of Solar Advisers Australia — trusted commercial & car wash solar specialists for over 4,000 businesses Australia-wide." image={IMG.installer} />
      <section className="bg-[var(--ink)] border-y border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display font-black text-5xl lg:text-7xl text-white">{s.plain ? s.v : <Counter value={s.v} suffix={s.suffix} />}</p>
              <p className="mt-1 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>
      <section data-testid="team-section" className="bg-[var(--concrete)] text-[var(--ink)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-24 lg:py-32">
          <p className="eyebrow eyebrow-dark">Leadership</p>
          <LineReveal onView lines={["Call Steve or Mark", "— 24/7."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl lg:text-7xl" />
          <div className="mt-14 grid md:grid-cols-2 gap-4">
            {CONTACT.people.map((p, i) => <Person key={p.id} p={p} i={i} />)}
          </div>
        </div>
      </section>
      <section data-testid="regions-section" className="bg-[var(--navy)]">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Operational, engineering &amp; installation teams</p>
            <LineReveal onView lines={["A team on the", "ground — wherever", "your carwash is."]} className="mt-5 font-display font-black uppercase leading-[0.88] text-5xl sm:text-6xl" />
            <FadeUp>
              <div className="mt-8 flex items-start gap-3 text-slate-300">
                <MapPin className="text-[var(--sun)] mt-1 shrink-0" size={18} />
                <p>National Head Office<br /><span className="text-white">{CONTACT.office}</span></p>
              </div>
            </FadeUp>
          </div>
          <ul className="lg:col-span-7 divide-y divide-white/10 border-y border-white/10">
            {REGIONS.map((r, i) => (
              <FadeUp key={r.name} delay={i * 0.03}>
                <li data-testid={`region-${i}`} className="group flex items-center justify-between gap-4 py-5 transition-colors hover:bg-white/[0.03] px-2">
                  <div>
                    <p className="font-display font-bold uppercase text-2xl sm:text-3xl group-hover:text-[var(--sun)] transition-colors">{r.name}</p>
                    {r.team && <p className="text-sm text-slate-400 mt-1">{r.team}</p>}
                  </div>
                  <span className="font-mono text-xs tracking-[0.2em] text-[var(--solar-light)] shrink-0">{r.state}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
        </div>
      </section>
      <Testimonials />
      <CtaBand />
    </div>
  );
}
